import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, Phone, Mail, Paintbrush, ChevronDown } from 'lucide-react'

export default function Header() {
  const [isOpen, setIsOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false)
  const [services, setServices] = useState([])
  const location = useLocation()
  const dropdownRef = useRef(null)

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/services')
        if (res.ok) {
          const data = await res.json()
          setServices(data)
        }
      } catch (error) {
        console.error('Fejl ved hentning af ydelser til header:', error)
      }
    }
    fetchServices()
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close dropdowns on route change
  useEffect(() => {
    setIsOpen(false)
    setIsDropdownOpen(false)
    setIsMobileServicesOpen(false)
  }, [location])

  // Prevent background scrolling when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const navItems = [
    { name: 'Forside', path: '/' },
    { name: 'Ydelser', path: '/ydelser', hasDropdown: true },
    { name: 'Galleri', path: '/galleri' },
    { name: 'Prisestimater', path: '/prisestimater' },
    { name: 'Om os', path: '/om-os' },
    { name: 'Kontakt', path: '/kontakt' }
  ]

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  return (
    <>
      {/* Top bar with contact info - NOT sticky */}
      <div className="hidden bg-white border-b border-slate-100 py-2 text-xs text-slate-600 sm:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-6">
            <a href="tel:+4592900091" className="flex items-center gap-1.5 hover:text-black transition-colors">
              <Phone className="h-3.5 w-3.5 text-black" />
              <span>+45 92 90 00 91</span>
            </a>
            <a href="mailto:info@probyg.tech" className="flex items-center gap-1.5 hover:text-black transition-colors">
              <Mail className="h-3.5 w-3.5 text-black" />
              <span>info@probyg.tech</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span>Probyg Maler ApS · CVR: 46347870</span>
            <span className="h-3 w-px bg-slate-200" />
            <span>Man - Fre: 07:00 - 16:00</span>
          </div>
        </div>
      </div>

      {/* Sticky Main navigation */}
      <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white">
        {/* Main navigation */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-20 items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group">
              <Paintbrush className="h-7 w-7 text-sky-600 fill-sky-100 transition-transform group-hover:rotate-12" />
              <div>
                <span className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 transition-colors group-hover:text-black">
                  Probyg <span className="text-slate-900">Maler</span>
                </span>
              </div>
            </Link>

            {/* Desktop Nav (Visible on lg and up) */}
            <nav className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => {
                if (item.hasDropdown) {
                  return (
                    <div
                      key={item.path}
                      className="relative"
                      ref={dropdownRef}
                      onMouseEnter={() => setIsDropdownOpen(true)}
                      onMouseLeave={() => setIsDropdownOpen(false)}
                    >
                      <button
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className={`inline-flex items-center gap-1 text-sm font-medium transition-colors hover:text-slate-900 focus:outline-none py-2 ${
                          isActive(item.path) ? 'text-slate-900 font-semibold' : 'text-slate-600'
                        }`}
                      >
                        <span>{item.name}</span>
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Dropdown Menu */}
                      {isDropdownOpen && (
                        <div className="absolute left-0 top-full z-50 w-64 rounded-xl border border-slate-200 bg-white p-2 shadow-lg ring-1 ring-black/5 animate-in fade-in slide-in-from-top-1 duration-150">
                          {services.length > 0 && (
                            <div className="space-y-0.5">
                              {services.map((service) => (
                                <Link
                                  key={service.id}
                                  to={`/ydelser/${service.slug?.da}`}
                                  className="block rounded-lg px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                                >
                                  {service.title?.da}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                }

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`text-sm font-medium transition-colors hover:text-slate-900 ${
                      isActive(item.path) ? 'text-slate-900 font-semibold' : 'text-slate-600'
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              })}
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 transition-all hover:shadow-md"
              >
                Få gratis tilbud
              </Link>
            </nav>

            {/* Hamburger menu button (Visible on mobile & tablet - below lg) */}
            <div className="flex lg:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                type="button"
                className="inline-flex items-center justify-center text-slate-700 focus:outline-none"
                aria-controls="mobile-menu"
                aria-expanded={isOpen}
              >
                <span className="sr-only">Åbn menu</span>
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Backdrop overlay for Sidebar */}
        <div
          className={`fixed inset-0 z-50 bg-black/40 backdrop-blur-xs transition-opacity duration-300 lg:hidden ${
            isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          onClick={() => setIsOpen(false)}
        />

        {/* Sliding Sidebar Menu (Mobile & Tablet) */}
        <div
          className={`fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-white p-6 shadow-2xl transition-transform duration-300 ease-in-out transform lg:hidden flex flex-col justify-between ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Sidebar Header */}
          <div className="flex items-center justify-between pb-6 border-b border-slate-100">
            <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center gap-2 group">
              <div>
                <span className="text-xl font-bold tracking-tight text-slate-900">
                  Probyg <span className="text-slate-900">Maler</span>
                </span>
              </div>
            </Link>
            <button
              onClick={() => setIsOpen(false)}
              type="button"
              className="text-slate-500 focus:outline-none"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Sidebar Navigation & Content */}
          <div className="flex-1 py-6 overflow-y-auto space-y-6">
            <nav className="space-y-1.5">
              {navItems.map((item) => {
                if (item.hasDropdown) {
                  return (
                    <div key={item.path} className="space-y-1">
                      <button
                        onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                        className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-base font-medium hover:bg-slate-50 hover:text-slate-900 transition-colors ${
                          isActive(item.path) ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-700'
                        }`}
                      >
                        <span>{item.name}</span>
                        <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {isMobileServicesOpen && (
                        <div className="pl-4 space-y-1 border-l border-slate-100 ml-3 mt-1">
                          {services.map((service) => (
                            <Link
                              key={service.id}
                              to={`/ydelser/${service.slug?.da}`}
                              onClick={() => setIsOpen(false)}
                              className="block rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                            >
                              {service.title?.da}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                }

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`block rounded-lg px-3 py-2.5 text-base font-medium hover:bg-slate-50 hover:text-slate-900 transition-colors ${
                      isActive(item.path) ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-700'
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              })}
            </nav>
          </div>

          {/* Sidebar Footer */}
          <div className="pt-6 border-t border-slate-100 space-y-6">
            <Link
              to="/kontakt"
              onClick={() => setIsOpen(false)}
              className="flex w-full items-center justify-center rounded-full bg-slate-900 py-3.5 text-center text-base font-semibold text-white shadow-sm hover:bg-black transition-colors"
            >
              Få gratis tilbud
            </Link>
            <div className="flex flex-col gap-3 text-sm text-slate-600">
              <a href="tel:+4592900091" className="flex items-center gap-2.5 py-1 hover:text-black transition-colors">
                <Phone className="h-4 w-4 text-black" />
                <span>+45 92 90 00 91</span>
              </a>
              <a href="mailto:info@probyg.tech" className="flex items-center gap-2.5 py-1 hover:text-black transition-colors">
                <Mail className="h-4 w-4 text-black" />
                <span>info@probyg.tech</span>
              </a>
              <div className="text-xs text-slate-400 pt-1">
                <span>Probyg Maler ApS · CVR: 46347870</span>
                <span className="block mt-1">Man - Fre: 07:00 - 16:00</span>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  )
}
